import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShowBanner(true);
    } else {
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        initializeAnalytics(savedPreferences.analytics);
      } catch (error) {
        // If stored data is corrupted, reset to default and show banner
        console.error("Failed to parse cookie preferences:", error);
        localStorage.removeItem("cookieConsent");
        setShowBanner(true);
      }
    }

    // Listen for custom event to open preferences
    const handleOpenPreferences = () => {
      setShowBanner(true);
      setShowPreferences(true);
    };

    window.addEventListener('openCookiePreferences', handleOpenPreferences);

    return () => {
      window.removeEventListener('openCookiePreferences', handleOpenPreferences);
    };
  }, []);

  const initializeAnalytics = (enabled: boolean) => {
    if (enabled && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    initializeAnalytics(prefs.analytics);
    setShowBanner(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(necessaryOnly);
    savePreferences(necessaryOnly);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      {!showPreferences && (
        <div className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6">
          <Card className="mx-auto max-w-4xl border-border bg-card shadow-elevated">
            <CardHeader className="relative pb-4">
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-2 h-8 w-8"
                onClick={acceptNecessary}
                aria-label="Close cookie banner and accept necessary cookies only"
              >
                <X className="h-4 w-4" />
              </Button>
              <CardTitle className="text-xl text-foreground">Cookie Preferences</CardTitle>
              <CardDescription className="text-muted-foreground">
                We use cookies to enhance your browsing experience, serve personalised content, and analyse our traffic. 
                By clicking "Accept All", you consent to our use of cookies.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 sm:flex-row">
              <Button
                onClick={() => setShowPreferences(true)}
                variant="outline"
                className="flex-1"
              >
                Manage Preferences
              </Button>
              <Button
                onClick={acceptNecessary}
                variant="outline"
                className="flex-1"
              >
                Necessary Only
              </Button>
              <Button
                onClick={acceptAll}
                className="flex-1 bg-gradient-hero text-primary-foreground hover:opacity-90"
              >
                Accept All
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preferences Dialog */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-2xl border-border bg-card shadow-elevated">
            <CardHeader>
              <CardTitle className="text-2xl text-foreground">Cookie Preferences</CardTitle>
              <CardDescription className="text-muted-foreground">
                Choose which cookies you want to accept. You can change your preferences at any time.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
                <div className="flex-1">
                  <Label htmlFor="necessary" className="text-base font-semibold text-foreground">
                    Necessary Cookies
                  </Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Essential cookies required for the website to function properly. These cannot be disabled.
                  </p>
                </div>
                <Switch
                  id="necessary"
                  checked={preferences.necessary}
                  disabled
                  className="mt-1"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
                <div className="flex-1">
                  <Label htmlFor="analytics" className="text-base font-semibold text-foreground">
                    Analytics Cookies
                  </Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                </div>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                  className="mt-1"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
                <div className="flex-1">
                  <Label htmlFor="marketing" className="text-base font-semibold text-foreground">
                    Marketing Cookies
                  </Label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.
                  </p>
                </div>
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, marketing: checked })
                  }
                  className="mt-1"
                />
              </div>

              <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                <Button
                  onClick={() => setShowPreferences(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveCustomPreferences}
                  className="flex-1 bg-gradient-hero text-primary-foreground hover:opacity-90"
                >
                  Save Preferences
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default CookieConsent;

// Add TypeScript declaration for gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      ...args: any[]
    ) => void;
  }
}
